'use client';

import { useEffect, useRef, useState } from 'react';

import Link from 'next/link';

import {
  ArrowDown,
  ArrowRight,
  Calculator,
  CheckCircle,
  DollarSign,
  FileText,
  Network,
  Search,
  Ship,
  TrendingUp,
} from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';
import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';

import appConfig from '~/config/app.config';
import pathsConfig from '~/config/paths.config';

import { RelatedTools } from '../_components/related-tools';

const baseUrl = appConfig.url;
const productName = appConfig.name;
const calculatorUrl = `${baseUrl}/tools/import-duty-calculator`;
const toolsUrl = `${baseUrl}/tools`;

const TRANSPORT_MODES = [
  { label: 'Ocean, HMF applies', value: 'ocean_hmf' },
  { label: 'Ocean, HMF exempt / not covered', value: 'ocean_exempt' },
  { label: 'Air freight', value: 'air' },
  { label: 'Road or rail', value: 'land' },
] as const;

const ENTRY_TYPES = [
  { label: 'Formal entry', value: 'formal' },
  { label: 'Formal, MPF exempt', value: 'formal_mpf_exempt' },
  { label: 'Informal, automated ($2.69)', value: 'informal_automated' },
  { label: 'Informal, manual ($8.06)', value: 'informal_manual' },
  {
    label: 'Informal, CBP-prepared ($12.09)',
    value: 'informal_cbp_prepared',
  },
] as const;

type FormData = {
  productValue: string;
  hsCode: string;
  transportMode: string;
  entryType: string;
  additionalTariffRate: string;
};

type CalculationResult = {
  productValue: number;
  htsCode: string;
  productDescription: string;
  generalRateLabel: string;
  dutyRate: number;
  baseDuty: number;
  additionalTariff: number;
  additionalTariffRate: number;
  harborFee: number;
  processingFee: number;
  totalDuties: number;
  effectiveRate: number;
  landedCost: number;
  transportMode: string;
  entryType: string;
  rateSource: string;
  breakdown: {
    productValue: number;
    duties: number;
    total: number;
  };
};

const heroHighlights = [
  'Live USITC base-duty lookup',
  'Current FY2026 US entry fees',
  'No signup required to calculate',
] as const;

const calculationSteps = [
  {
    title: '1. HS Code Classification',
    description:
      'Your product classification sets the base duty rate. Small mistakes can materially change the estimate.',
    icon: Search,
  },
  {
    title: '2. Country of Origin',
    description:
      'Origin changes eligibility for trade programs and additional tariffs, including Section 301 for some US imports.',
    icon: Ship,
  },
  {
    title: '3. Customs Value & Fees',
    description:
      'The estimate applies the selected FY2026 entry-processing fee and adds the harbor fee only when you select a covered-port ocean movement.',
    icon: FileText,
  },
] as const;

const whyItMatters = [
  {
    title: 'See the entry bill before the PO',
    description:
      'A live HTS rate plus CBP fees is the number to compare to selling price, not the factory quote.',
  },
  {
    title: 'Leave room for Section 301',
    description:
      'The lookup is the Column 1 General rate. Enter the extra percent yourself when you already know a Chapter 99 measure applies.',
  },
  {
    title: 'Give finance the same stack',
    description:
      'Ops and finance work from one HTS lookup and fee schedule, instead of two spreadsheets that disagree.',
  },
] as const;

const faqItems = [
  {
    value: 'accuracy',
    question: 'How accurate is this import duty calculator?',
    answer:
      'The calculator looks up the current Column 1 General rate for an exact US HTS code from the USITC and applies the selected FY2026 CBP entry-processing fee. It does not automatically determine trade-agreement eligibility, antidumping or countervailing duties, Section 232, Section 301, or other Chapter 99 measures. Enter a known additional tariff rate when one applies, and confirm the final entry with a licensed customs broker.',
  },
  {
    value: 'hs-code',
    question: 'What is an HS code?',
    answer:
      'The Harmonized System code is the classification customs authorities use to determine duty treatment. A small classification mistake can materially change the duty estimate.',
  },
  {
    value: 'additional-tariffs',
    question: 'Does the estimate include Section 301 or other extra tariffs?',
    answer:
      'Only when you enter an applicable rate expressed against the full customs value. These measures change frequently and depend on exact Chapter 99 eligibility, exclusions, entry dates, and sometimes a narrower value basis, so the calculator does not guess them from a short HS-code prefix.',
  },
  {
    value: 'landed-cost',
    question: 'Does this show my full landed cost?',
    answer:
      'It shows customs value plus estimated base duty, any additional tariff rate you enter, and the selected US entry-processing fee. Freight, insurance, brokerage, taxes, and destination charges still need to be added in the landed cost calculator.',
  },
  {
    value: 'china',
    question: 'Can I estimate import duty from China here?',
    answer:
      'Yes, if you have the US HTS code and the customs value. The live lookup is the Column 1 General rate. China-origin goods often also carry Section 301 or other Chapter 99 layers — enter that extra percent yourself. The method, worked numbers, and what to collect before you classify are in the import-duty-from-China guide.',
  },
  {
    value: 'worked-example',
    question: 'What does a simple US entry look like?',
    answer:
      'A $10,000 formal ocean entry at a 7.5% general rate is $750 duty before fees. FY2026 merchandise processing fee is 0.3464% of customs value ($33.58 minimum, $651.50 maximum), and harbor maintenance is 0.125% on a covered-port ocean movement — $34.64 MPF and $12.50 HMF on that $10,000 entry. Those fees are why a "7.5% duty" quote is not the number that hits the entry. Confirm the live HTS line and any Chapter 99 measure with a licensed broker before you file.',
  },
] as const;

function StructuredData() {
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: "US Import Duty Calculator: What You'll Owe Before the PO",
    description:
      'Look up a live USITC HTS rate, add FY2026 CBP entry fees, and see the duty bill before you place the PO.',
    url: calculatorUrl,
    applicationCategory: 'BusinessApplication',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    creator: {
      '@type': 'Organization',
      name: productName,
      url: baseUrl,
    },
    featureList: [
      'Live USITC HTS base-duty lookup',
      'Estimate US customs duties',
      'Formal and informal entry fee options',
      'Optional additional tariff input',
      'Transport-aware harbor maintenance fee calculations',
      'FY2026 merchandise processing fee calculations',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: baseUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Free Tools',
        item: toolsUrl,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Import Duty Calculator',
        item: calculatorUrl,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webAppSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}

function formatCurrency(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '--';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function downloadCsvReport(results: CalculationResult) {
  const rows = [
    ['Metric', 'Value'],
    ['US HTS code', results.htsCode],
    ['Product description', results.productDescription],
    ['Destination country', 'United States'],
    ['Transport mode', results.transportMode],
    ['Entry type', results.entryType],
    ['Customs value (USD)', results.productValue.toFixed(2)],
    ['Base duty rate', results.generalRateLabel],
    ['Rate source', results.rateSource],
    ['Base duty amount', results.baseDuty.toFixed(2)],
    ['Additional tariff rate', `${results.additionalTariffRate}%`],
    ['Additional tariff amount', results.additionalTariff.toFixed(2)],
    ['Harbor maintenance fee', results.harborFee.toFixed(2)],
    ['Merchandise processing fee', results.processingFee.toFixed(2)],
    ['Total duties and fees', results.totalDuties.toFixed(2)],
    ['Effective duty rate', `${results.effectiveRate}%`],
    ['Customs value plus duties and fees', results.landedCost.toFixed(2)],
  ];

  const escapeCsvCell = (value: string) => `"${value.replaceAll('"', '""')}"`;
  const csv = rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `import-duty-estimate-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function ImportDutyCalculatorPage() {
  const [formData, setFormData] = useState<FormData>({
    productValue: '',
    hsCode: '',
    transportMode: 'ocean_hmf',
    entryType: 'formal',
    additionalTariffRate: '0',
  });
  const [results, setResults] = useState<CalculationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resultsRef = useRef<HTMLElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const calculationIdRef = useRef(0);

  const startFreePath = pathsConfig.auth.startFree;
  const hasResults = results !== null;
  const landedCost = results?.landedCost ?? results?.breakdown?.total;

  useEffect(() => {
    if (!results) return;

    const frame = window.requestAnimationFrame(() => {
      const reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;
      resultsRef.current?.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
      resultsRef.current?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [results]);

  useEffect(() => {
    if (!error) return;

    errorRef.current?.focus();
  }, [error]);

  function updateField<K extends keyof FormData>(key: K, value: FormData[K]) {
    calculationIdRef.current += 1;
    setResults(null);
    setLoading(false);
    setFormData((current) => ({
      ...current,
      [key]: value,
    }));
  }

  async function calculateDuty(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResults(null);
    const calculationId = ++calculationIdRef.current;
    const submittedData = { ...formData };

    try {
      const response = await fetch('/api/tools/calculate-duty', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submittedData),
      });

      const data = (await response.json()) as CalculationResult & {
        error?: string;
      };

      if (calculationId !== calculationIdRef.current) return;

      if (!response.ok) {
        setError(data.error ?? 'Calculation failed. Please try again.');
        setResults(null);
        return;
      }

      setResults(data);
    } catch (calculationError) {
      if (calculationId !== calculationIdRef.current) return;

      console.error('Calculation error:', calculationError);
      setError('Something went wrong. Please try again.');
      setResults(null);
    } finally {
      if (calculationId === calculationIdRef.current) {
        setLoading(false);
      }
    }
  }

  return (
    <>
      <StructuredData />

      <div className="bg-background text-foreground min-h-screen">
        <section className="border-border bg-muted relative overflow-hidden border-b">
          <div className="absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(circle_at_top_right,_color-mix(in_srgb,var(--primary)_calc(0.12*100%),transparent),_transparent_42%)]" />
          <div className="bg-primary/10 absolute -top-28 right-0 h-72 w-72 rounded-full blur-3xl" />

          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid items-start gap-12 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="flex flex-col gap-6 pt-2 lg:pt-10">
                <div className="bg-primary/10 text-primary inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase">
                  <Calculator className="h-4 w-4" />
                  Import Cost Planning Tool
                </div>

                <div className="space-y-5">
                  <h1 className="font-heading text-foreground max-w-2xl text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                    <span className="text-primary">
                      US Import Duty Calculator
                    </span>
                    : What You&apos;ll Owe Before the PO
                  </h1>

                  <p className="text-muted-foreground max-w-xl text-lg leading-8">
                    Enter an HTS code and customs value. The calculator looks up
                    the live USITC base rate, adds FY2026 CBP entry fees, and
                    shows the duty bill before you place the PO.
                  </p>
                </div>

                <div className="flex flex-wrap gap-5 pt-2">
                  {heroHighlights.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <CheckCircle className="text-primary h-5 w-5" />
                      <span className="text-muted-foreground text-sm font-medium">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-border bg-card rounded-[10px] border p-6 shadow-[0_20px_50px_-28px_rgba(22,22,42,0.35)] sm:p-8">
                <div className="mb-6 flex items-center gap-3">
                  <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-xl">
                    <Calculator className="text-primary h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-foreground text-xl font-bold">
                      Estimate Import Costs
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      Customs value, duty logic, and import-cost impact
                    </p>
                  </div>
                </div>

                <form onSubmit={calculateDuty} className="space-y-5">
                  <div className="space-y-2">
                    <Label
                      htmlFor="hsCode"
                      className="text-muted-foreground text-sm font-bold"
                    >
                      Product Category / HS Code
                    </Label>
                    <div className="relative">
                      <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                      <Input
                        id="hsCode"
                        value={formData.hsCode}
                        onChange={(e) => updateField('hsCode', e.target.value)}
                        placeholder="e.g. 3926.90.25.00"
                        inputMode="numeric"
                        required
                        className="border-border bg-muted h-12 rounded-xl pl-10 shadow-none"
                      />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-muted-foreground text-xs">
                        Enter the complete 8 or 10-digit US code.
                      </p>
                      <a
                        href="https://hts.usitc.gov/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                      >
                        Find your code at USITC
                        <ArrowRight className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label
                        htmlFor="entryType"
                        className="text-muted-foreground text-sm font-bold"
                      >
                        Entry Type
                      </Label>
                      <Select
                        value={formData.entryType}
                        onValueChange={(value) =>
                          updateField('entryType', value)
                        }
                      >
                        <SelectTrigger
                          id="entryType"
                          className="border-border bg-muted h-12 rounded-xl shadow-none"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ENTRY_TYPES.map((entryType) => (
                            <SelectItem
                              key={entryType.value}
                              value={entryType.value}
                            >
                              {entryType.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-xs leading-5">
                        Most commercial shipments over $2,500 use a formal
                        entry. Confirm the filing type with your broker.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="transportMode"
                        className="text-muted-foreground text-sm font-bold"
                      >
                        Transport Mode
                      </Label>
                      <Select
                        value={formData.transportMode}
                        onValueChange={(value) =>
                          updateField('transportMode', value)
                        }
                      >
                        <SelectTrigger
                          id="transportMode"
                          className="border-border bg-muted h-12 rounded-xl shadow-none"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TRANSPORT_MODES.map((mode) => (
                            <SelectItem key={mode.value} value={mode.value}>
                              {mode.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="productValue"
                      className="text-muted-foreground text-sm font-bold"
                    >
                      Customs Value (USD)
                    </Label>
                    <Input
                      id="productValue"
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      value={formData.productValue}
                      onChange={(e) =>
                        updateField('productValue', e.target.value)
                      }
                      placeholder="0.00"
                      className="border-border bg-muted h-12 rounded-xl shadow-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="additionalTariffRate"
                      className="text-muted-foreground text-sm font-bold"
                    >
                      Additional Full-Value Tariff Rate (%)
                      <span className="font-normal"> (optional)</span>
                    </Label>
                    <Input
                      id="additionalTariffRate"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.additionalTariffRate}
                      onChange={(e) =>
                        updateField('additionalTariffRate', e.target.value)
                      }
                      className="border-border bg-muted h-12 rounded-xl shadow-none"
                    />
                    <p className="text-muted-foreground text-xs leading-5">
                      Enter only a rate that applies to the full customs value.
                      Do not enter a Section 232 rate based only on
                      metal-content value; use its effective full-value rate
                      instead.
                    </p>
                  </div>

                  {error ? (
                    <p
                      ref={errorRef}
                      tabIndex={-1}
                      role="alert"
                      className="text-destructive text-sm font-medium outline-none"
                    >
                      {error}
                    </p>
                  ) : null}

                  <Button
                    type="submit"
                    disabled={loading}
                    className={`text-primary-foreground h-12 w-full rounded-xl text-base font-bold shadow-lg transition-colors ${
                      hasResults
                        ? 'bg-emerald-600 shadow-emerald-600/20 hover:bg-emerald-600'
                        : 'bg-primary shadow-primary/20 hover:bg-accent-deep/90'
                    }`}
                  >
                    {loading ? (
                      'Checking the current tariff...'
                    ) : hasResults ? (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Calculation Complete
                      </>
                    ) : (
                      'Calculate US Import Duty'
                    )}
                  </Button>

                  {hasResults ? (
                    <p
                      className="flex items-center justify-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-400"
                      role="status"
                    >
                      <ArrowDown className="h-4 w-4" />
                      Your verified estimate is shown below
                    </p>
                  ) : null}

                  <p className="text-muted-foreground text-xs leading-5">
                    US estimate using the USITC Column 1 General rate and your
                    selected entry type. Trade programs and special duties
                    require separate verification.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section
          ref={resultsRef}
          tabIndex={-1}
          aria-live="polite"
          aria-label="Import duty calculation results"
          data-test="duty-results"
          className={`border-border scroll-mt-20 border-y py-12 transition-colors duration-500 outline-none ${
            hasResults ? 'bg-emerald-50/70 dark:bg-emerald-950/20' : 'bg-card'
          }`}
        >
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="border-primary/10 bg-primary/[0.06] rounded-[10px] border p-6 sm:p-8">
              <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-6">
                  <div className="space-y-2">
                    <p className="text-muted-foreground text-sm font-bold tracking-[0.22em] uppercase">
                      Calculation Results
                    </p>
                    <h3 className="font-heading text-foreground text-2xl font-bold">
                      {hasResults
                        ? 'United States import estimate'
                        : 'Run a calculation to see your live estimate'}
                    </h3>
                  </div>

                  <div className="flex flex-col gap-6 sm:flex-row sm:items-end">
                    <div>
                      <p className="text-muted-foreground text-xs font-bold tracking-[0.2em] uppercase">
                        Effective Duty &amp; Fee Rate
                      </p>
                      <p className="text-primary text-3xl font-black sm:text-4xl">
                        {hasResults ? `${results.effectiveRate}%` : '--'}
                      </p>
                    </div>

                    <div className="border-border border-l pl-0 sm:pl-6">
                      <p className="text-muted-foreground text-xs font-bold tracking-[0.2em] uppercase">
                        Value + Duties &amp; Fees
                      </p>
                      <p className="text-foreground text-3xl font-black sm:text-4xl">
                        {formatCurrency(landedCost)}
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="border-border bg-card/70 rounded-2xl border p-4">
                      <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                        Base Duty
                      </p>
                      <p className="text-foreground mt-2 text-lg font-bold">
                        {hasResults ? formatCurrency(results.baseDuty) : '--'}
                      </p>
                    </div>

                    <div className="border-border bg-card/70 rounded-2xl border p-4">
                      <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                        Additional Tariffs
                      </p>
                      <p className="text-foreground mt-2 text-lg font-bold">
                        {hasResults
                          ? formatCurrency(results.additionalTariff)
                          : '--'}
                      </p>
                    </div>

                    <div className="border-border bg-card/70 rounded-2xl border p-4">
                      <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                        Fees
                      </p>
                      <p className="text-foreground mt-2 text-lg font-bold">
                        {hasResults
                          ? formatCurrency(
                              results.harborFee + results.processingFee,
                            )
                          : '--'}
                      </p>
                    </div>
                  </div>

                  {hasResults ? (
                    <div className="border-border bg-card/80 rounded-2xl border p-4 text-sm leading-6">
                      <p className="text-foreground font-bold">
                        HTS {results.htsCode}: {results.productDescription}
                      </p>
                      <p className="text-muted-foreground mt-1">
                        Base rate: {results.generalRateLabel} from{' '}
                        {results.rateSource}. The processing fee reflects the
                        selected entry type. Harbor maintenance is{' '}
                        {results.transportMode === 'ocean_hmf'
                          ? 'included based on the selected covered-port option'
                          : 'not applied based on the selected transport option'}
                        .
                      </p>
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => results && downloadCsvReport(results)}
                    disabled={!hasResults}
                    className="text-primary disabled:text-muted-foreground dark:disabled:text-muted-foreground text-sm font-semibold disabled:cursor-not-allowed"
                  >
                    Download CSV summary
                  </button>
                </div>
              </div>

              {hasResults ? (
                <div className="border-primary/20 bg-card mt-8 rounded-[8px] border p-6 shadow-sm sm:p-8">
                  <div className="grid items-center gap-6 lg:grid-cols-[1fr_auto]">
                    <div>
                      <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
                        Go beyond one-off estimates
                      </p>
                      <h3 className="font-heading text-foreground mt-2 text-2xl font-bold">
                        Keep every PO, shipment, document, and landed cost in
                        one place
                      </h3>
                      <p className="text-muted-foreground mt-3 max-w-3xl text-sm leading-7">
                        {productName} turns supplier documents and shipment data
                        into a shared operating workspace, so purchasing,
                        operations, and finance can track real costs without
                        rebuilding spreadsheets for every order.
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                      <Button asChild className="h-11 rounded-xl font-bold">
                        <Link href={startFreePath}>
                          Start Your 14-Day Trial
                        </Link>
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        className="h-11 rounded-xl font-bold"
                      >
                        <Link href="/#features">See How It Works</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto mb-16 max-w-3xl text-center">
              <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                How this import duty calculator works
              </h2>
              <p className="text-muted-foreground mt-4 text-base leading-7">
                Classification sets the base rate. You enter any extra tariff
                you already know applies. Fees follow the FY2026 CBP schedule
                and the transport mode you pick.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {calculationSteps.map((step) => {
                const Icon = step.icon;

                return (
                  <div
                    key={step.title}
                    className="border-border bg-card rounded-[8px] border p-8 shadow-sm"
                  >
                    <div className="bg-primary/10 mb-6 flex h-12 w-12 items-center justify-center rounded-xl">
                      <Icon className="text-primary h-5 w-5" />
                    </div>
                    <h3 className="text-foreground text-xl font-bold">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground mt-3 text-sm leading-7">
                      {step.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-foreground text-background relative overflow-hidden py-24 sm:py-32">
          <div className="bg-primary/20 absolute -top-48 -right-32 h-[520px] w-[520px] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-white/5 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div className="max-w-xl">
              <p className="text-primary text-xs font-bold tracking-[0.24em] uppercase">
                The estimate is free. The insight keeps going.
              </p>
              <h2 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Your duty is one number. Your supply chain is the whole story.
              </h2>
              <p className="text-background/70 mt-6 text-lg leading-8">
                {productName} keeps purchase orders, supplier updates,
                documents, shipments, and landed costs in one calm workspace.
                Everyone sees what is happening, what it costs, and what needs
                attention next.
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  className="bg-background text-foreground hover:bg-background/90 h-12 rounded-xl px-7 font-bold"
                >
                  <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="text-background hover:bg-background/10 h-12 rounded-xl border-white/25 bg-transparent px-7 font-bold"
                >
                  <Link href="/#features">See {productName}</Link>
                </Button>
              </div>

              <div className="text-background/60 mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                {['No credit card', 'Live in 15 minutes', 'Cancel anytime'].map(
                  (item) => (
                    <span key={item} className="flex items-center gap-2">
                      <CheckCircle className="text-primary h-4 w-4" />
                      {item}
                    </span>
                  ),
                )}
              </div>
            </div>

            <figure className="bg-background text-foreground rounded-[32px] p-3 shadow-[0_32px_90px_-32px_rgba(0,0,0,0.7)] sm:p-4">
              <figcaption className="text-muted-foreground px-3 pb-3 text-xs font-bold tracking-[0.16em] uppercase">
                Illustrative {productName} workspace
              </figcaption>
              <div className="border-border bg-card overflow-hidden rounded-[8px] border">
                <div className="border-border flex items-center justify-between border-b px-5 py-4 sm:px-7">
                  <div className="flex items-center gap-3">
                    <div className="bg-foreground text-background flex h-9 w-9 items-center justify-center rounded-xl">
                      <Network className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-extrabold">{productName}</p>
                      <p className="text-muted-foreground text-xs">
                        Example order overview
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    On track
                  </div>
                </div>

                <div className="p-5 sm:p-7">
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-muted-foreground text-xs font-bold tracking-[0.18em] uppercase">
                        Purchase order SA-1048
                      </p>
                      <p className="font-heading mt-2 text-3xl font-black sm:text-4xl">
                        $18,426.00
                      </p>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Current landed cost
                      </p>
                    </div>
                    <div className="rounded-full bg-emerald-100 px-4 py-2 text-sm font-bold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                      Ready to ship
                    </div>
                  </div>

                  <div className="mt-8 h-2 overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
                    <div className="bg-primary h-full w-[82%] rounded-full" />
                  </div>
                  <div className="text-muted-foreground mt-2 flex justify-between text-xs">
                    <span>Order placed</span>
                    <span>Shipment ready</span>
                  </div>

                  <div className="mt-8 grid gap-3 sm:grid-cols-3">
                    <div className="bg-muted rounded-2xl p-4">
                      <FileText className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">Ready</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Documents organized
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <Ship className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">In progress</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Supplier status
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <DollarSign className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">Visible</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Landed cost
                      </p>
                    </div>
                  </div>

                  <div className="border-border mt-6 flex items-center gap-3 border-t pt-5">
                    <CheckCircle className="text-primary h-5 w-5 shrink-0" />
                    <p className="text-sm font-semibold">
                      Purchasing, operations, and finance now see the same
                      number.
                    </p>
                  </div>
                </div>
              </div>
            </figure>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="border-border relative overflow-hidden rounded-[10px] border bg-[radial-gradient(circle_at_center,var(--primary)_0%,var(--primary)_34%,var(--primary)_100%)] p-10 shadow-[0_20px_50px_-28px_rgba(22,22,42,0.35)]">
                <div className="absolute inset-0 [background-image:linear-gradient(rgba(255,255,255,0.16)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.16)_1px,transparent_1px)] [background-size:18px_18px] opacity-20" />
                <div className="relative flex aspect-[4/3] items-center justify-center">
                  <div className="absolute h-72 w-72 rounded-full border border-white/10" />
                  <div className="absolute h-56 w-56 rounded-full border border-white/15" />
                  <div className="absolute h-40 w-40 rounded-full border border-white/20" />
                  <div className="bg-card/10 flex h-28 w-28 items-center justify-center rounded-full border border-white/20 shadow-2xl backdrop-blur-sm">
                    <Network className="text-primary-foreground h-12 w-12" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                  Why the Duty Number Has to Land Before the PO
                </h2>

                <div className="mt-8 space-y-6">
                  {whyItMatters.map((item) => (
                    <div key={item.title} className="flex gap-4">
                      <div className="bg-sage-fg/10 text-sage-fg mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                        <CheckCircle className="h-4 w-4" />
                      </div>
                      <div>
                        <h3 className="text-foreground text-lg font-bold">
                          {item.title}
                        </h3>
                        <p className="text-muted-foreground mt-1 text-sm leading-7">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="border-border bg-card rounded-2xl border p-5">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-xl">
                        <TrendingUp className="text-primary h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm font-semibold">
                          Margin planning
                        </p>
                        <p className="text-foreground text-lg font-bold">
                          Estimate landed cost earlier
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-border bg-card rounded-2xl border p-5">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-xl">
                        <DollarSign className="text-primary h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm font-semibold">
                          Cost visibility
                        </p>
                        <p className="text-foreground text-lg font-bold">
                          Keep fees in the buying model
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-card py-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-foreground text-center text-3xl font-bold sm:text-4xl">
              Frequently Asked Questions
            </h2>

            <Accordion type="single" collapsible className="mt-12 space-y-4">
              {faqItems.map((faq) => (
                <AccordionItem
                  key={faq.value}
                  value={faq.value}
                  className="border-border rounded-[8px] border px-6"
                >
                  <AccordionTrigger className="text-foreground text-left text-base font-bold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6 text-sm leading-7">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-16">
              <h2 className="text-foreground text-2xl font-bold">
                Related reading
              </h2>
              <ul className="text-muted-foreground mt-4 space-y-3 text-sm leading-7">
                <li>
                  <Link
                    href="/blog/calculate-import-duty-from-china"
                    className="text-primary font-semibold hover:underline"
                  >
                    How to calculate import duty from China
                  </Link>{' '}
                  — HS/HTS, customs value, Section 301, and a worked landed-cost
                  example.
                </li>
                <li>
                  <Link
                    href="/tools/landed-cost-calculator"
                    className="text-primary font-semibold hover:underline"
                  >
                    Landed cost calculator
                  </Link>{' '}
                  — add freight after you have a duty rate.
                </li>
                <li>
                  <Link
                    href="/blog/how-to-calculate-landed-cost"
                    className="text-primary font-semibold hover:underline"
                  >
                    How to calculate landed cost
                  </Link>{' '}
                  — duty is one line; the unit cost is the whole stack.
                </li>
              </ul>
            </div>

            <RelatedTools currentPath="/tools/import-duty-calculator" />
          </div>
        </section>

        <section className="relative overflow-hidden py-24">
          <div className="bg-primary/15 absolute top-1/2 left-0 h-64 w-64 -translate-y-1/2 rounded-full blur-3xl" />
          <div className="bg-primary/10 absolute right-0 bottom-0 h-80 w-80 rounded-full blur-3xl" />

          <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="bg-primary text-primary-foreground rounded-[32px] px-6 py-12 text-center shadow-[0_20px_50px_-24px_var(--primary)] sm:px-12">
              <h2 className="font-heading text-4xl font-extrabold tracking-tight">
                Ready to Automate Your Supply Chain?
              </h2>
              <p className="text-primary-foreground/85 mx-auto mt-5 max-w-2xl text-lg leading-8">
                The import duty calculator is the starting point. {productName}{' '}
                connects suppliers, costs, documents, and landed-cost visibility
                in one workspace.
              </p>

              <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
                <Button
                  asChild
                  className="text-accent-deep bg-card hover:bg-muted h-12 rounded-xl px-8 font-bold"
                >
                  <Link href={startFreePath}>Start Your 14-Day Free Trial</Link>
                </Button>

                <Button
                  asChild
                  variant="outline"
                  className="text-primary-foreground hover:bg-card/10 h-12 rounded-xl border-white/30 bg-transparent px-8 font-bold"
                >
                  <Link href="/contact">Talk to us</Link>
                </Button>
              </div>

              <p className="text-primary-foreground/75 mt-8 text-sm">
                No credit card required • Instant setup • Unlimited calculations
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
